import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { api } from "../../api";
import { PROBLEM_FIELDS, problemProgramFields, snakeToCamel } from "../../admin/fields";
import { ListEditor } from "../../admin/ListEditor";
import { RecordForm } from "../../admin/RecordForm";
import { SourcesPanel } from "../../admin/SourcesPanel";
import { VersionsPanel } from "../../admin/VersionsPanel";

type Item = { id: string; slug: string; title: string; active: number };
type Raw = {
  problem: Record<string, unknown> & { id: string; slug: string; title: string; active: number };
  lists: { faqs: Record<string, unknown>[]; programs: { program_id: string; rank: number; reason: string | null }[] };
};
type Cat = { id: string; name: string; children: { id: string; name: string }[] };
type Card = { id: string; name: string; status: string };

export function ProblemsAdmin() {
  const { id } = useSearch({ from: "/app/admin/problems" });
  const navigate = useNavigate({ from: "/admin/problems" });
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const [err, setErr] = useState("");
  const list = useQuery({ queryKey: ["kb-problems"], queryFn: () => api<{ problems: Item[] }>("/api/admin/kb/problems") });
  const raw = useQuery({ queryKey: ["problem-raw", id], enabled: !!id && id !== "new", queryFn: () => api<Raw>(`/api/admin/kb/problems/${id}/raw`) });
  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api<{ categories: Cat[] }>("/api/directory/categories") });
  // Every program (the directory list stops at 500); under "programs" so program edits refresh it.
  const progs = useQuery({ queryKey: ["programs", "kb-options"], queryFn: () => api<{ programs: Card[] }>("/api/admin/kb/program-options") });
  const categoryOptions = (cats.data?.categories ?? []).flatMap((c) => [{ value: c.id, label: c.name },
    ...c.children.map((s) => ({ value: s.id, label: `${c.name} › ${s.name}` }))]);
  const programOptions = (progs.data?.programs ?? []).map((p) => ({ value: p.id, label: p.status === "discontinued" ? `${p.name} (discontinued)` : p.name }));
  const reload = () => {
    for (const k of ["kb-problems", "problem-raw", "problem", "problems", "versions", "sources", "queue", "search", "suggest"]) qc.invalidateQueries({ queryKey: [k] });
  };
  const shown = (list.data?.problems ?? []).filter((p) => p.title.toLowerCase().includes(filter.toLowerCase()));
  const d = raw.data;

  return (
    <div className="mx-auto grid max-w-6xl gap-4 lg:grid-cols-3">
      <section className="card flex flex-col gap-2">
        <div className="flex items-center justify-between"><h1 className="text-lg font-semibold">Customer problems</h1>
          <button className="btn-primary" onClick={() => navigate({ search: { id: "new" } })}>New</button></div>
        <input className="input" placeholder="Filter" aria-label="Filter problems" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <ul className="max-h-[70vh] overflow-auto text-sm">{shown.map((p) => (
          <li key={p.id}>
            <button className={`w-full rounded px-2 py-1 text-left hover:bg-slate-100 dark:hover:bg-slate-800 ${p.id === id ? "bg-brand-50 font-semibold" : ""}`}
              onClick={() => navigate({ search: { id: p.id } })}>
              {p.title}{!p.active && <span className="ml-1 text-xs text-slate-500">(hidden)</span>}
            </button>
          </li>
        ))}</ul>
      </section>

      <div className="flex flex-col gap-4 lg:col-span-2">
        {id === "new" && (
          <section className="card"><h2 className="mb-1 font-semibold">New problem</h2>
            <p className="mb-3 text-sm text-slate-500">A new problem starts as pending: guidance, alternatives, follow-up and FAQs stay hidden from reps until it has a source and is verified. The summary, questions to ask and information to collect show at once, without a source, so they never state eligibility rules, amounts, limits or deadlines.</p>
            <RecordForm fields={PROBLEM_FIELDS} sendAll initial={{}} submitLabel="Create problem" selectOptions={{ categoryId: categoryOptions }}
              onSave={async (v) => { const r = await api<{ id: string }>("/api/admin/kb/problems", { method: "POST", body: v }); reload(); navigate({ search: { id: r.id } }); }} />
          </section>
        )}
        {d && (
          <>
            <section className="card">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-semibold">{d.problem.title}</h2>
                <div className="flex items-center gap-2">
                  <Link to="/problems/$slug" params={{ slug: d.problem.slug }} search={{}} className="text-sm text-brand-700 hover:underline">View as a rep</Link>
                  <button className="btn-ghost text-xs" onClick={async () => {
                    try { await api(`/api/admin/kb/problems/${id}`, { method: "PATCH", body: { active: !d.problem.active } }); setErr(""); reload(); }
                    catch (e) { setErr((e as Error).message); }
                  }}>{d.problem.active ? "Hide from reps" : "Show to reps"}</button>
                </div>
              </div>
              {err && <p role="alert" className="mb-2 text-sm text-red-600">{err}</p>}
              <p className="mb-3 text-xs text-slate-500">Summary, questions and information to collect show to reps without a source: no eligibility rules, amounts, limits or deadlines. Edited guidance, alternatives, follow-up and FAQs stay hidden until the problem is verified again.</p>
              <RecordForm key={id} fields={PROBLEM_FIELDS} initial={snakeToCamel(d.problem)} submitLabel="Save problem" selectOptions={{ categoryId: categoryOptions }}
                onSave={async (v) => { await api(`/api/admin/kb/problems/${id}`, { method: "PATCH", body: v }); reload(); }} />
            </section>
            <section className="card"><h3 className="mb-1 text-sm font-semibold">Programs to check, in order</h3>
              <p className="mb-3 text-xs text-slate-500">The first program is suggested first (discontinued ones always show last, under "Ended programs"). Say why it fits; never put eligibility rules, amounts, limits or deadlines here: reps see the reason without a source.</p>
              {progs.data ? (
                <ListEditor key={`${id}--programs`} kind="programs" fields={problemProgramFields(programOptions)}
                  items={d.lists.programs.map((p) => ({ programId: p.program_id, reason: p.reason }))}
                  onSave={async (items) => { await api(`/api/admin/kb/problems/${id}/programs`, { method: "PUT", body: items }); reload(); }} />
              ) : progs.error ? <p role="alert" className="text-sm text-red-600">{(progs.error as Error).message}</p>
                : <p className="text-sm text-slate-500">Loading programs…</p>}
            </section>
            <section className="card"><h3 className="mb-3 text-sm font-semibold">FAQs</h3>
              <ListEditor key={`${id}--faqs`} kind="faqs" items={d.lists.faqs.map(snakeToCamel)}
                onSave={async (items) => { await api(`/api/admin/kb/problems/${id}/faqs`, { method: "PUT", body: items }); reload(); }} />
            </section>
            <section className="card"><SourcesPanel entityType="problem" entityId={id!} /></section>
            <section className="card"><h3 className="mb-2 text-sm font-semibold">History</h3><VersionsPanel type="problem" id={id!} base="/api/admin/kb" /></section>
          </>
        )}
        {(list.error || raw.error) && <p role="alert" className="text-sm text-red-600">{((list.error ?? raw.error) as Error).message}</p>}
        {!id && <p className="text-sm text-slate-500">Choose a problem, or create a new one.</p>}
      </div>
    </div>
  );
}
